import { LoadingOutlined } from '@ant-design/icons';
import { DefaultOptionType } from '@bugly/common';
import { TdsFieldSelectorEnableExcludeValue } from '@tencent/tds-ui/src/query-form';
import { useControllableValue } from 'ahooks';
import { Checkbox, Select, SelectProps, Switch, Tag } from 'antd';
import classNames from 'classnames';
import { isArray } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useStores } from '@/presentation/store';
import { FieldKey, FieldScope, getLocalField } from '@/proto/field';
import styles from './index.module.scss';

export interface TdsFieldSelectorEnableExcludeProps extends SelectProps {
  fieldKey?: string;
  fieldScope?: string | number;
  optionTransform?: (option: DefaultOptionType[]) => DefaultOptionType[];
  // 是否初始加载下拉框数据,用于处理value和label不一致，页面刷新时，没有下拉数据，导致显示错误
  isDefaultQueryOptions?: boolean;
  // 不获取本地配置数据, 处理field name冲突问题
  forceRemote?: boolean;
  triggerOnHide?: boolean;
}

export const TdsFieldSelectorEnableExclude = (props: TdsFieldSelectorEnableExcludeProps) => {
  // 传给外部的value
  const [value, setValue] = useControllableValue<TdsFieldSelectorEnableExcludeValue>(props, {
    defaultValue: {
      select_value: undefined,
      switch_value: undefined,
    },
  });
  // 内部处理的value
  const [stashValue, setStashValue] = useState<TdsFieldSelectorEnableExcludeValue>({
    select_value: value?.select_value,
    switch_value: value?.switch_value,
  });

  const {
    className,
    fieldKey,
    fieldScope,
    options,
    dropdownClassName,
    optionTransform,
    isDefaultQueryOptions,
    forceRemote = false,
    ...restProps
  } = props;
  const { loadField } = useStores().FieldStore;
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [innerOptions, setInnerOptions] = useState<DefaultOptionType[]>(options || []);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [switchDisabled, setSwitchDisabled] = useState<boolean>(false);// 控制排除按钮是否可用
  // const [switchChecked, setSwitchChecked] = useState<boolean>();// 控制排除按钮是否打开
  const [optionLen, setOptionLen] = useState(0);
  const [remoteOptions, setRemoteOptions] = useState<DefaultOptionType[]>([]);
  const [hasValueChanged, setHasValueChanged] = useState(false);
  const defaultFilterOption = (inputValue: string, option: any) => {
    const valueList = inputValue?.split?.(/;|,/);
    if (valueList?.length > 1) {
      return valueList?.includes?.(option.label);
    }
    return option?.label?.toLowerCase?.()?.indexOf?.(inputValue?.toLowerCase?.() || '') >= 0;
  };

  /**
   * 展开时加载
   * @param open
   */
  const onDropdownVisibleChange = async (open: boolean) => {
    setDropdownVisible(open);
    if (open) {
      await queryOptions();
    }
  };

  const queryOptions = async () => {
    let field = getLocalField(fieldKey as FieldKey);
    if (field && !forceRemote) {
      return;
    }
    const needRemote = (fieldKey && fieldScope && !options);
    if (needRemote && !loaded) {
      setLoading(true);
      field = await loadField(fieldKey as FieldKey, fieldScope as FieldScope);
      setRemoteOptions(field);
      const fieldConvert = field?.map(fieldItem => ({
        label: optionRender(fieldItem),
        value: fieldItem.value,
        labelString: fieldItem.label,
      }));
      setInnerOptions(fieldConvert || []);
      setLoading(false);
      setLoaded(true);
    }
  };

  const initOptions = () => {
    if (remoteOptions.length !== 0) {
      const fieldConvert = remoteOptions?.map(fieldItem => ({
        label: optionRender(fieldItem),
        value: fieldItem.value,
        labelString: fieldItem.label,
      }));
      setInnerOptions(fieldConvert || []);
      return;
    }
    const field = getLocalField(fieldKey as FieldKey);
    const fieldConvert = field?.map(fieldItem => ({
      label: optionRender(fieldItem),
      value: fieldItem.value,
      labelString: fieldItem.label,
    }));
    setInnerOptions(fieldConvert || []);
  };


  const dropdownRender = (originNode: any) => {
    if (loading) {
      return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}><LoadingOutlined /></div>;
    }
    {
      return (
        <div className={styles.select}>
          {originNode}
          <div className={styles.footer} >
            排除:
            <Switch
              // name={`${fieldKey}_operator_convert`}
              // label={<span style={{ paddingLeft: '12px' }}>排除</span>}
              onChange={ onSwitchChange }
              disabled={switchDisabled}
              checked ={stashValue.switch_value}
              // defaultChecked={value.switch_value}
              className={styles.footerSwitch}
            />
            <span className={styles.footerTag}>
              已
              {stashValue.switch_value
                ? '排除:'
                : '包含:'}
              {optionLen}项
            </span>
          </div>
        </div>
      );
    }
  };

  // 渲染option
  const optionRender = (fieldItem: any) =>  {
    let checkedState ;
    if (isArray(stashValue?.select_value)) {
      checkedState = (stashValue?.select_value)?.some((val: any) => val === fieldItem.value);
    }
    return (
      <div className={styles.optionRender}>
        <Checkbox
          className={styles.checkboxRender}
          checked={checkedState}
        />
        <span className={styles.labelRender}>{fieldItem.label}</span>
      </div>
    );
  };

  // 渲染render
  const tagRender = (props: any) => {
    const {  closable, onClose } = props;
    return (<Tag
    className={styles.tagRender}
    closable={closable}
    onClose={onClose}>
      {innerOptions?.find(option => option.value === props.value)?.labelString || props.label}
    </Tag>);
  };

  const onSelectChange = (select_value: any) => {
    if ((isArray(select_value) && select_value?.length === 0) || !select_value) {
      setStashValue({
        ...stashValue,
        select_value,
        switch_value: false,
      });
      setHasValueChanged(true);
      setSwitchDisabled(true);
      return;
    }
    setStashValue({
      ...stashValue,
      select_value,
    });

    setHasValueChanged(true);
  };

  const onSwitchChange = (switch_value: any) => {
    setStashValue({
      ...stashValue,
      switch_value,
    });
    setHasValueChanged(true);
  };

  useEffect(() => {
    initOptions();
    if ((isArray(stashValue?.select_value) && stashValue?.select_value?.length === 0) || !stashValue?.select_value) {
      setSwitchDisabled(true);
    } else {
      setSwitchDisabled(false);
    }
    // 处理选中数据数量的逻辑
    if (isArray(stashValue?.select_value)) {
      setOptionLen(stashValue?.select_value?.length);
    }
  }, [options, stashValue?.select_value]);

  // 刷新后stashValue未能马上获取到token解析出的value，需要监听value的变化
  useEffect(() => {
    setStashValue({ ...value });
  }, [value]);

  useEffect(() => {
    if (!dropdownVisible && hasValueChanged && props.triggerOnHide) {
      setValue({ ...stashValue });
      setHasValueChanged(false);
    }
  }, [dropdownVisible, hasValueChanged]);

  return (
    <Select
      placeholder={'请选择'}
      className={classNames('tds-form-select', className, classNames({
        [styles.commentRenderInclude]: !stashValue.switch_value,
        [styles.commentRenderNot]: stashValue.switch_value,
      }))}
      allowClear
      showArrow
      options={innerOptions.filter(item => !!item.value).map(item => ({
        ...item,
        ['data-tid']: `${name}-${item.label}`,
      }))}
      dropdownClassName={classNames('tds-form-select-dropdown', dropdownClassName)}
      showSearch
      autoClearSearchValue
      filterOption={defaultFilterOption}
      maxTagCount='responsive'
      onDropdownVisibleChange={onDropdownVisibleChange}
      dropdownRender={dropdownRender}
      tagRender={tagRender}
      {...restProps}
      value={stashValue.select_value }
      onChange={onSelectChange}
    />
  );
};
